/*
 * Powerlete category rail — in-place collection swap.
 *
 * Rebuilds the live theme's "tap a category, swap the grid without a full reload"
 * behaviour (live used a bespoke swapCollectionSection()), but the idiomatic Horizon
 * way: Shopify's Section Rendering API + native section re-init.
 *
 * On tapping a category circle (a same-origin /collections/ link), we fetch the target
 * collection with `?sections=<main>,<banner>` (JSON of rendered section HTML), then
 * replace the current main-collection section and banner in place, push the URL, and
 * update the active circle. Horizon's Web Components (results-list, facets, product
 * cards) re-initialise automatically via connectedCallback when the new nodes are
 * inserted, so filtering/sorting/quick-add keep working and filters reset to the new
 * collection (correct).
 *
 * Progressive enhancement: if there is no collection results-list on the page, or the
 * fetch fails, or the browser lacks what we need, we fall back to a normal navigation —
 * the links are real <a href> links, so nothing is ever broken. Wrapped in a View
 * Transition when supported (and motion is allowed) for a smooth cross-fade.
 */
class PowerleteCategoryAjax extends HTMLElement {
  connectedCallback() {
    this._onClick = this._onClick.bind(this);
    this._onPrefetch = this._onPrefetch.bind(this);
    this._onPopState = this._onPopState.bind(this);

    this.addEventListener('click', this._onClick);
    // Prefetch on intent (hover / touch start) so a tap feels instant on slow collections.
    this.addEventListener('pointerenter', this._onPrefetch, { capture: true });
    this.addEventListener('touchstart', this._onPrefetch, { capture: true, passive: true });
    window.addEventListener('popstate', this._onPopState);

    this._cache = new Map();
    this._busy = false;

    // Stamp the initial history entry as ours. Our own navigations push { pwCategory }, but the
    // first entry has a null state; without this stamp the popstate guard below would ignore a
    // Back that lands on the initial collection. Non-destructively merges into any existing state.
    if (!history.state || !history.state.pwCategory) {
      try {
        history.replaceState({ ...history.state, pwCategory: window.location.href }, '', window.location.href);
      } catch (e) {
        /* replaceState can throw in rare sandboxed contexts; detection still works, just no stamp */
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this._onPopState);
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // The collection results-list lives in a sibling section; bail (normal nav) if absent.
  get mainSection() {
    const results = document.querySelector('results-list');
    return results ? results.closest('[id^="shopify-section-"]') : null;
  }

  get bannerSection() {
    const banner = document.querySelector('.pw-cb');
    return banner ? banner.closest('[id^="shopify-section-"]') : null;
  }

  _linkFromEvent(event) {
    const link = event.target.closest('.pw-catrail__link');
    if (!link || !this.contains(link)) return null;
    return link;
  }

  _eligible(link, event) {
    if (!link) return null;
    if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button)) return null;
    const href = link.getAttribute('href');
    if (!href || href === '#') return null;
    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return null;
    }
    if (url.origin !== window.location.origin) return null;
    return url.href;
  }

  _onPrefetch(event) {
    const link = this._linkFromEvent(event);
    const url = this._eligible(link, null);
    if (!url || this._cache.has(url)) return;
    if (!this.mainSection) return;
    // Fire and forget; store the promise so navigate() can await the same request.
    this._cache.set(url, this._fetchSections(url).catch(() => null));
  }

  _onClick(event) {
    const link = this._linkFromEvent(event);
    const url = this._eligible(link, event);
    if (!url) return;
    // Only enhance when there is actually a collection grid to swap.
    if (!this.mainSection) return;
    event.preventDefault();
    this._navigate(url, link, true);
  }

  _sectionIds() {
    const main = this.mainSection;
    if (!main) return null;
    const ids = { main: main.id.replace('shopify-section-', '') };
    const banner = this.bannerSection;
    if (banner) ids.banner = banner.id.replace('shopify-section-', '');
    return ids;
  }

  async _fetchSections(url) {
    const ids = this._sectionIds();
    if (!ids) throw new Error('no-main');
    const list = ids.banner ? `${ids.main},${ids.banner}` : ids.main;
    const join = url.indexOf('?') === -1 ? '?' : '&';
    const res = await fetch(`${url}${join}sections=${encodeURIComponent(list)}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!res.ok) throw new Error('bad-response');
    const data = await res.json();
    return { ids, data };
  }

  async _navigate(url, link, push) {
    if (this._busy) return;
    this._busy = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const pending = this._cache.get(url) || this._fetchSections(url);
      const payload = await pending;
      if (!payload) throw new Error('no-payload');
      const { ids, data } = payload;

      const mainHtml = data[ids.main];
      if (!mainHtml) throw new Error('missing-main');

      const apply = () => {
        this._replaceSection(ids.main, mainHtml);
        if (ids.banner && data[ids.banner]) {
          this._replaceSection(ids.banner, data[ids.banner]);
        }
        if (link) this._setActive(link);
        if (push) history.pushState({ pwCategory: url }, '', url);
        this._announce(link);
      };

      if (document.startViewTransition && !this.reducedMotion) {
        document.startViewTransition(apply);
      } else {
        apply();
      }
    } catch (e) {
      // Never leave the shopper stuck — fall back to a real navigation.
      window.location.href = url;
    } finally {
      this._busy = false;
      this.removeAttribute('aria-busy');
    }
  }

  _replaceSection(id, html) {
    const current = document.getElementById(`shopify-section-${id}`);
    if (!current) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.getElementById(`shopify-section-${id}`);
    if (next) current.replaceWith(next);
  }

  _setActive(activeLink) {
    this.querySelectorAll('.pw-catrail__link').forEach((el) => {
      const isActive = el === activeLink;
      el.classList.toggle('is-active', isActive);
      if (isActive) {
        el.setAttribute('aria-current', 'page');
      } else {
        el.removeAttribute('aria-current');
      }
    });
  }

  _announce(link) {
    let region = this.querySelector('[data-pw-cat-status]');
    if (!region) {
      region = document.createElement('span');
      region.setAttribute('data-pw-cat-status', '');
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.className = 'visually-hidden';
      this.appendChild(region);
    }
    const label = link ? (link.querySelector('.pw-catrail__label')?.textContent || '').trim() : '';
    if (label) region.textContent = label;
  }

  _onPopState(event) {
    // Only handle pops of OUR OWN history entries (stamped with pwCategory, incl. the initial
    // entry stamped in connectedCallback). Facet and infinite-scroll controllers push their own
    // states on the same collection URL; without this guard we re-fetched and replaced the grid on
    // their pops too, fighting them.
    if (!event || !event.state || !event.state.pwCategory) return;
    // Back/forward: re-sync to the URL now in the address bar.
    if (!this.mainSection) return;
    const url = window.location.href;
    const link = Array.from(this.querySelectorAll('.pw-catrail__link')).find((el) => {
      const href = el.getAttribute('href');
      if (!href) return false;
      try {
        return new URL(href, window.location.origin).href === url;
      } catch (e) {
        return false;
      }
    });
    this._navigate(url, link || null, false);
  }
}

if (!customElements.get('powerlete-category-ajax')) {
  customElements.define('powerlete-category-ajax', PowerleteCategoryAjax);
}
